import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { RetailLookupType } from '@pms/shared';
import {
  createRetailLookup,
  deleteRetailLookup,
  fetchRetailLookups,
  updateRetailLookup,
  type CreateRetailLookupPayload,
  type ListRetailLookupsQuery,
  type UpdateRetailLookupPayload,
} from '@/services/api/retailLookups.api';

export function useRetailLookups(query: ListRetailLookupsQuery, enabled = true) {
  return useQuery({
    queryKey: ['retail-lookups', query.type, query],
    queryFn: () => fetchRetailLookups(query),
    staleTime: 30_000,
    enabled: enabled && !!query.propertyId,
  });
}

export function useCreateLookup(type: RetailLookupType) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRetailLookupPayload) => createRetailLookup(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['retail-lookups', type] });
      void queryClient.invalidateQueries({ queryKey: ['retail-products'] });
    },
  });
}

export function useUpdateLookup(type: RetailLookupType) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateRetailLookupPayload }) =>
      updateRetailLookup(type, id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['retail-lookups', type] });
      void queryClient.invalidateQueries({ queryKey: ['retail-products'] });
    },
  });
}

export function useDeleteLookup(type: RetailLookupType) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteRetailLookup(type, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['retail-lookups', type] });
      void queryClient.invalidateQueries({ queryKey: ['retail-products'] });
    },
  });
}
